import { CacheRoute, createRouter } from '../../sw/router'
import { DownloadCache } from './queue'

/**
 * The worker's cache names, keyed by the route it would give a URL. The downloader writes into the same
 * caches the worker reads, so a downloaded file is served the same way one browsed into the cache is.
 */
const CACHE_NAMES: Partial<Record<CacheRoute, string>> = {
	[CacheRoute.Content]: 'content',
	[CacheRoute.Media]: 'media',
}

export type Router = ReturnType<typeof createRouter>

export const contentRouter = (): Router =>
	createRouter({ imagesHost: ENV.BUNNY_DOMAIN, uploadsHost: ENV.BUNNY_UPLOADS_DOMAIN })

/** A cache that files each URL where the worker will look for it, rather than into one bucket of its own. */
export const routingCache = (
	caches: CacheStorage,
	routeFor: Router = contentRouter(),
): DownloadCache & { nameFor: (key: string) => string | undefined } => {
	const nameFor = (key: string): string | undefined => CACHE_NAMES[routeFor(key, { method: 'GET' })]

	const open = async (key: string): Promise<Cache | undefined> => {
		const name = nameFor(key)

		return name ? caches.open(name).catch(() => undefined) : undefined
	}

	return {
		nameFor,
		match: async key => {
			const cache = await open(key)
			// Every cache key is versioned or signed, so a Vary mismatch would only ever hide a real hit.
			return cache?.match(key, { ignoreVary: true }).catch(() => undefined)
		},
		put: async (key, response) => {
			const cache = await open(key)
			// A URL the worker would not cache is one it will never serve, so storing it would be a lie.
			if (!cache) throw new Error(`No cache serves ${key}`)

			await cache.put(key, response)
		},
		delete: async key => {
			const cache = await open(key)

			return (await cache?.delete(key).catch(() => false)) ?? false
		},
	}
}
